import React, { useState, useEffect, useContext } from "react";
import { Routes, Route } from "react-router-dom";
import HomeHeroUI from "./home/HomeHeroUI";
import Locations from "./locations/Locations";
import VideoDisplayHeroUI from "./videos/VideoDisplayHeroUI";
import VideoPage from "./videos/VideoPage";
import ProfilePage from "./components/ProfilePage";
import NotFound from "./components/NotFound";
import ThemePreview from "./themes/ThemePreview";
import TypographyExample from "./components/TypographyExample";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import FirstVisitBanner from "./components/FirstVisitBanner";
import VersionUpdateModal from "./components/VersionUpdateModal";
import ErrorBoundary from "./components/ErrorBoundary";
import ConfigErrorScreen from "./components/ConfigErrorScreen";
import "./App.css";
import "react-tooltip/dist/react-tooltip.css";
import { AuthProvider, AuthContext } from "./contexts/AuthContext";
import { UploadModalProvider, useUploadModal } from "./contexts/UploadModalContext";
import { ThemeProvider, HeroUIThemeProvider } from "./themes";
import { ThemeAwareHeroUIProvider } from "./components/ThemeAwareHeroUIProvider";
import UploadModalHeroUI from "./components/UploadModalHeroUI";
import { isFirebaseConfigured, missingEnvVars } from "./config/firebase";
import "./themes/themes.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:8080';

const AppContent = () => {
  const { currentUser, userProfile, getIdToken } = useContext(AuthContext);
  const { showUploadModal } = useUploadModal();
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [legalVersions, setLegalVersions] = useState({
    termsVersion: '1.0.0',
    privacyVersion: '1.0.0',
  });

  useEffect(() => {
    const checkLegalVersions = async () => {
      // ✅ FIX: Wait for BOTH currentUser AND userProfile to prevent race condition
      // This ensures MongoDB profile is fully created before checking legal versions
      // Fixes: "user profile not found" error in terms modal during Google sign-in
      if (!currentUser || !userProfile) return;

      try {
        const token = await getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/auth/legal-versions`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setLegalVersions({
            termsVersion: data.termsVersion,
            privacyVersion: data.privacyVersion,
          });
          setShowVersionModal(data.requiresUpdate);
        }
      } catch (error) {
      }
    };

    checkLegalVersions();
  }, [currentUser, userProfile, getIdToken]);

  const handleAcceptVersions = async (termsVersion, privacyVersion) => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/auth/accept-legal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          termsVersion,
          privacyVersion,
        }),
      });

      if (response.ok) {
        setShowVersionModal(false);
      } else {
        const error = await response.json();
        alert(error.message || 'Failed to accept legal versions');
      }
    } catch (error) {
      alert('Failed to accept legal versions. Please try again.');
    }
  };

  // Enhanced mobile detection
  const isMobile =
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    ('ontouchstart' in window) ||
    (window.innerWidth <= 768);

  // On mobile, when upload modal is open, only show the upload modal
  if (isMobile && showUploadModal) {
    return <UploadModalHeroUI />;
  }

  return (
    <>
      <FirstVisitBanner />
      <VersionUpdateModal
        isOpen={showVersionModal}
        termsVersion={legalVersions.termsVersion}
        privacyVersion={legalVersions.privacyVersion}
        onAccept={handleAcceptVersions}
      />
      <div className="w-full">
                <Routes>
            <Route path="/" element={<HomeHeroUI />} />
            <Route path="/locations" element={<Locations />} />
            <Route
              path="/state/:encodedStateName"
              element={<VideoDisplayHeroUI />}
            />
            {/* SEO-friendly slug-based URLs */}
            <Route path="/clips/:slug" element={<VideoPage />} />
            {/* Legacy ID-based URLs - kept for backward compatibility */}
            <Route path="/video/:videoId" element={<VideoPage />} />
            <Route path="/v/:videoId" element={<VideoPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/theme-preview" element={<ThemePreview />} />
            <Route path="/typography" element={<TypographyExample />} />
                  <Route path="/terms" element={<TermsOfService />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
      </div>
      <UploadModalHeroUI />
    </>
  );
};

const App = () => {
  // Check if Firebase is properly configured before rendering the app
  if (!isFirebaseConfigured) {
    return (
      <ThemeProvider>
        <ThemeAwareHeroUIProvider>
          <HeroUIThemeProvider>
            <ConfigErrorScreen missingVars={missingEnvVars} />
          </HeroUIThemeProvider>
        </ThemeAwareHeroUIProvider>
      </ThemeProvider>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <ThemeAwareHeroUIProvider>
            <HeroUIThemeProvider>
              <UploadModalProvider>
                <AppContent />
              </UploadModalProvider>
            </HeroUIThemeProvider>
          </ThemeAwareHeroUIProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
